import { EventEmitter } from 'eventemitter3';
import { ErrorData, LevelDetails, LoadStats, Events, Fragment } from 'hls.js';

import { VideogularComponent } from '../../player/videogular/Videogular.Component';
import { DUAL_PLAYBACK_LAYOUT_OPTIONS } from '../../player/DualPlaybackLayoutOptions';

import {
	HLS_PLAYER_BUFFERING,
	HLS_PLAYER_CAN_PLAY,
	HLS_PLAYER_LEVEL_LOADED,
	HLS_PLAYER_LEVELS_UPDATED
} from '../HlsPlayer';
import { IRevConnectInfo } from '@vbrick/vbrick-rev-connect/src/IRevConnectInfo';
import { IHlsPlayerLevelsUpdatedData } from '../IHlsPlayerLevelsUpdatedData';

import {
	MasterPlaylistLoader,
	MPL_PLAYLIST_PARSED,
	MPL_PLAYLIST_PARSING_ERROR,
	MPL_PLAYLIST_LOAD_ERROR
} from './MasterPlaylistLoader';
import {
	CONTENT_SYNC_THRESHOLD_SECS,
	MASTER_PLAYLIST_REFRESH_INTERVAL_SECS,
	MAX_INVALID_SYNC_EXPECTED_CONTENT_TIME_COUNT,
	SECONDS_NUMBER_PRECISION,
	SPEAKER_PLAYLIST_CONTENT_TAG
} from './DualHlsConstants';
import { ContentCueTrack } from './ContentCueTrack';
import { ContentCueTrackEvent } from './ContentCueTrackEvent';
import { ContentTrackTypes } from './ContentTrackTypes';
import { DualHlsContentPlayer } from './DualHlsContentPlayer';
import { DualHlsPlayer } from './DualHlsPlayer';
import { IContentPlaylist } from './IContentPlaylist';
import { IMasterPlaylistData } from './IMasterPlaylistData';
import { parseVbrickContentTags } from './PlaylistUtils';
import { ContentCue } from './ContentCue';
import { ILevelsUpdated } from './ILevelsUpdatedData';

enum SyncIssue {
	CONTENT_NO_CLOSEST_FRAG = -3,
	NO_SPEAKER_TIME = -2,
	SPEAKER_BEFORE_CONTENT = -1
}

export const LEVELS_UPDATED: string = 'levelsUpdated';
export const SYNC_ISSUE_NO_SPEAKER_TIME_THRESHOLD: number = 3;

export class DualPlaybackOrchestrator extends EventEmitter {
	private activeContentPlayer: DualHlsContentPlayer;
	private contentCueTrack: ContentCueTrack;
	private contentPlayerLevels: IHlsPlayerLevelsUpdatedData;
	private contentPlayers: Map<string, DualHlsContentPlayer>;
	private contentPlaylists: Map<string, IContentPlaylist>;
	private invalidSyncExpectedContentTimeCount: number;
	private isContentTrackedInMasterPlaylist: boolean;
	private isContentBuffering: boolean;
	private isSpeakerBuffering: boolean;
	private masterPlaylistLoader: MasterPlaylistLoader;
	private speakerPlayer: DualHlsPlayer;
	private speakerPlayerLevels: IHlsPlayerLevelsUpdatedData;
	private syncIssueNoSpeakerTimeCount: number = 0;

	private readonly speakerPlayerEventHandlers = [
		{ event: 'timeupdate', handler: () => this.onSpeakerPlayerTimeUpdate() },
		{ event: 'pause', handler: () => this.onSpeakerPlayerPause() },
		{ event: 'play', handler: () => this.onSpeakerPlayerPlay() }
	];

	constructor(
		private src: string,
		private contentPlayerContainer: HTMLElement,
		private isLive: boolean,
		private API: VideogularComponent,
		private revConnectInfo: IRevConnectInfo,
		private isMulticast: boolean
	) {
		super();

		const livePlaylistRefreshIntervalSecs: number = isLive ?
			MASTER_PLAYLIST_REFRESH_INTERVAL_SECS :
			null;

		this.contentPlayers = new Map<string, DualHlsContentPlayer>();
		this.invalidSyncExpectedContentTimeCount = 0;
		this.isContentBuffering = this.isSpeakerBuffering = false;
		this.masterPlaylistLoader = new MasterPlaylistLoader(this.API, src, isMulticast, livePlaylistRefreshIntervalSecs);

		//initialize loadding of the master playlist
		this.loadMasterPlaylist();

		document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);
	}

	public get bandwidth(): number {
		return this.speakerPlayer?.bwEstimate || null;
	}

	public get bitrate(): number {
		return this.speakerPlayer?.bitrate || null;
	}

	private get isDocumentVisible(): boolean {
		return !document.hidden;
	}

	private loadMasterPlaylist(): void {
		//attach events
		this.masterPlaylistLoader.on(MPL_PLAYLIST_PARSED, (masterPlaylistData: IMasterPlaylistData) => this.onMasterPlaylistParsed(masterPlaylistData));
		this.masterPlaylistLoader.on(MPL_PLAYLIST_PARSING_ERROR, () => this.onMasterPlaylistError());
		this.masterPlaylistLoader.on(MPL_PLAYLIST_LOAD_ERROR, () => this.onMasterPlaylistError());

		//start loading
		this.masterPlaylistLoader.start();
	}

	private onDocumentVisibilityChange = (): void => {
		if (!this.contentCueTrack) {
			return;
		}

		this.contentCueTrack.isActive = this.isDocumentVisible;

		// workaround for Safari pausing muted video on hide
		if (!this.isDocumentVisible &&
			this.speakerPlayer.videoElement.muted &&
			!this.speakerPlayer.isPlaying &&
			this.API.currentState === 'play') {

			window.setTimeout(() => this.speakerPlayer.play(), 1000);
		}
	};

	private onMasterPlaylistParsed({ contentPlaylists, isContentTrackedInMasterPlaylist, speakerPlaylist }: IMasterPlaylistData): void {
		// error state safety check
		if (this.API.activeSource.isUnavailable) {
			this.masterPlaylistLoader.stop();
			return;
		}

		this.isContentTrackedInMasterPlaylist = isContentTrackedInMasterPlaylist;

		if (this.contentPlaylists) { //update
			this.updateContentTracks(contentPlaylists);
		} else { //init
			this.contentPlaylists = contentPlaylists;

			this.initSpeakerPlayer(speakerPlaylist, isContentTrackedInMasterPlaylist);
		}
	}

	private onMasterPlaylistError(): void {
		this.API.onVideoError();
	}

	private initSpeakerPlayer(speakerPlaylist: string, isContentTrackedInMasterPlaylist: boolean): void {
		const isLive: boolean = this.API.activeSource.isLive;
		const isStartPosition: boolean = !isLive && !!this.API.startTime;

		this.speakerPlayer = new DualHlsPlayer(speakerPlaylist, this.API.mediaElement, this.API, isLive, this.isMulticast, this.revConnectInfo, {
			startPosition: isStartPosition ? this.API.startTime : -1
		});

		this.speakerPlayer.on(HLS_PLAYER_BUFFERING, () => this.onSpeakerPlayerBuffering());
		this.speakerPlayer.on(HLS_PLAYER_CAN_PLAY, () => this.onSpeakerPlayerCanPlay());
		this.speakerPlayer.once(HLS_PLAYER_LEVEL_LOADED, () => this.initContentTrack());
		this.speakerPlayer.on(HLS_PLAYER_LEVELS_UPDATED, (data: IHlsPlayerLevelsUpdatedData) => this.onSpeakerPlayerLevelsUpdated(data));

		if (!isContentTrackedInMasterPlaylist) { // then it's tracked in the speaker playlist
			this.speakerPlayer.on(HLS_PLAYER_LEVEL_LOADED, ({ details }: { details: LevelDetails }) => this.onSpeakerPlayerLevelLoaded(details));
		}

		//wire up to speaker player (time update to surface content, nonlinear detection/handling)
		this.speakerPlayerEventHandlers.forEach(eventHandler => this.speakerPlayer.videoElement.addEventListener(eventHandler.event, eventHandler.handler));
	}

	private onContentPlayerLevelsUpdated(data: IHlsPlayerLevelsUpdatedData): void {
		this.contentPlayerLevels = data;

		this.emitLevels();
	}

	private onSpeakerPlayerLevelsUpdated(data: IHlsPlayerLevelsUpdatedData): void {
		this.speakerPlayerLevels = data;

		this.emitLevels();

		// inform all content players of the new level
		this.contentPlayers.forEach(contentPlayer => contentPlayer.setLevel(data.activeLevelIndex));
	}

	private emitLevels(): void {
		const payload: ILevelsUpdated = {
			contentPlayerLevels: this.contentPlayerLevels,
			speakerPlayerLevels: this.speakerPlayerLevels
		};

		this.emit(LEVELS_UPDATED, payload);
	}

	private onSpeakerPlayerLevelLoaded(levelDetails: LevelDetails): void {
		if (!levelDetails.fragments || !levelDetails.fragments.length) {
			return;
		}

		const contentPlaylistTags = levelDetails.fragments[0].tagList
			.filter(([tagKey]) => tagKey === SPEAKER_PLAYLIST_CONTENT_TAG)
			.map(([, tagValue]) => tagValue);

		const contentPlaylists: Map<string, IContentPlaylist> = parseVbrickContentTags(contentPlaylistTags, this.src, this.isMulticast);

		this.updateContentTracks(contentPlaylists);

		if (this.activeContentPlayer && this.isContentLoadingAfterEnded()) {
			this.activeContentPlayer.stopActiveStreamLoading();
		}
	}

	private initContentTrack(): void {
		this.contentCueTrack = new ContentCueTrack(this.speakerPlayer, this.contentPlaylists, this.isDocumentVisible);
		this.contentCueTrack.on(ContentCueTrackEvent.ACTIVE_CUES_CHANGED, () => this.onTextTrackCueChange());
		this.speakerPlayer.resumeHelper.on('resume', () => this.onTextTrackCueChange());

		this.onTextTrackCueChange();
	}

	private updateContentTracks(updatedContentPlaylists: Map<string, IContentPlaylist>): void {
		this.contentPlaylists = updatedContentPlaylists;

		if (this.contentCueTrack) {
			updatedContentPlaylists.forEach(contentPlaylist => this.contentCueTrack.addOrUpdateContentPlaylist(contentPlaylist));
			this.contentCueTrack.isInitialPhase = false;
		}
	}

	private onTextTrackCueChange(): void {
		if (this.speakerPlayer.resumeHelper.isResumeInProgress) {
			return;
		}

		//clean up any content players that should not be active
		this.cleanUpContentPlayers();

		this.contentCueTrack.activeCues.forEach(cue => {
			//for either spinup or play type cases, we may need to spin up players
			const contentPlaylist: IContentPlaylist = cue.contentPlaylist;
			const contentPlayer: DualHlsContentPlayer = this.contentPlayers.get(this.getFormattedSecStr(contentPlaylist.start)) ||
				this.spinUpContentPlayer(cue);

			//initiate playback
			if (cue.type === ContentTrackTypes.PLAY) {
				this.setActiveContentPlayer(contentPlayer);
			} else if (cue.type === ContentTrackTypes.SPIN_UP && contentPlayer === this.activeContentPlayer) { //already active, but we've moved back to the spin up range
				this.cleanUpContentPlayer(contentPlayer, null, false);
			}
		});
	}

	private cleanUpContentPlayers() {
		this.contentPlayers.forEach((currentContentPlayer, transitionTime) => {
			const isActiveCueForTheContentPlaylist: boolean = this.contentCueTrack.activeCues.some(
				cue => cue.contentPlaylist.start === currentContentPlayer.cue.contentPlaylist.start
			);

			if (!isActiveCueForTheContentPlaylist) {
				this.cleanUpContentPlayer(currentContentPlayer, transitionTime, true);
			}
		});
	}

	private onSpeakerPlayerTimeUpdate(): void {
		//if there is an active content player, attempt to sync it up with the speaker player
		if (this.activeContentPlayer) {
			this.syncPlayers();
		}
	}

	private syncPlayers(): void {
		const expectedContentTime: number = this.currentSpeakerTimeToContentTimeInSecs();

		switch (expectedContentTime) {
			case SyncIssue.CONTENT_NO_CLOSEST_FRAG:
				if (this.activeContentPlayer.isReadyToSync) {
					this.onInvalidSyncExpectedContentTime();
					return;
				}
				break;

			case SyncIssue.NO_SPEAKER_TIME:
				if (this.syncIssueNoSpeakerTimeCount > SYNC_ISSUE_NO_SPEAKER_TIME_THRESHOLD) {
					console.warn(`Current speaker program dateTime could not be extracted. Threshold reached suggesting we have drifted too far, so seeking to live sync position.`);
					this.speakerPlayer.seekToLiveSyncPosition();
					this.syncIssueNoSpeakerTimeCount = 0;
				} else {
					console.warn(`Current speaker program dateTime could not be extracted (count = ${this.syncIssueNoSpeakerTimeCount})`);
					++this.syncIssueNoSpeakerTimeCount;
				}
				return;

			case SyncIssue.SPEAKER_BEFORE_CONTENT:
				console.warn('Current speaker program dateTime falls before the initial of activeContent');
				return;
		}

		this.syncIssueNoSpeakerTimeCount = 0;

		this.onValidSyncExpectedContentTime();

		if (!this.activeContentPlayer.isActive) {
			this.activeContentPlayer.surface(this.speakerPlayer.isPlaying);
		}

		const timeDifference: number = Math.max(expectedContentTime, this.activeContentPlayer.currentTime) - Math.min(expectedContentTime, this.activeContentPlayer.currentTime);

		if (this.activeContentPlayer.isReadyToSync && timeDifference >= CONTENT_SYNC_THRESHOLD_SECS) {
			console.info(`timeDifference = ${timeDifference}, so seeking to expectedContentTime`);

			if (!this.activeContentPlayer.isPlaying && this.API.currentState === 'play') {
				console.warn(`syncPlayers - caught content player not playing`);
				this.activeContentPlayer.play();
			}

			this.activeContentPlayer.seekTo(expectedContentTime);
		}
	}

	private currentSpeakerTimeToContentTimeInSecs(): number {
		const speakerDateTime: Date = this.speakerPlayer.getCurrentProgramDateTime();

		if (!speakerDateTime) {
			return SyncIssue.NO_SPEAKER_TIME;
		}

		if (speakerDateTime < this.activeContentPlayer.initialProgramDateTime) {
			return SyncIssue.SPEAKER_BEFORE_CONTENT;
		}

		const closestContentFragment: Fragment = this.activeContentPlayer.findClosestProgramDateTimeFragment(speakerDateTime);

		if (!closestContentFragment) {
			return SyncIssue.CONTENT_NO_CLOSEST_FRAG;
		}

		const timeDifferenceMs: number = speakerDateTime.getTime() - closestContentFragment.programDateTime;

		return closestContentFragment.start + (timeDifferenceMs / 1000);
	}

	private onValidSyncExpectedContentTime(): void {
		this.invalidSyncExpectedContentTimeCount = 0;
	}

	private onInvalidSyncExpectedContentTime(): void {
		++this.invalidSyncExpectedContentTimeCount;

		console.warn(`Invalid expected content sync time #${this.invalidSyncExpectedContentTimeCount}`);

		if (this.invalidSyncExpectedContentTimeCount >= MAX_INVALID_SYNC_EXPECTED_CONTENT_TIME_COUNT) {
			console.error(`Invalid expected content sync time has reached the max, so recovering content`);
			this.invalidSyncExpectedContentTimeCount = 0;
			this.activeContentPlayer.recover();
		}
	}

	private onSpeakerPlayerBuffering(): void {
		console.info('speaker buffering ' + this.speakerPlayer.videoElement.readyState);
		this.isSpeakerBuffering = true;

		this.onBufferingStateUpdated();
	}

	private onSpeakerPlayerCanPlay(): void {
		console.info('speaker canplay');
		//buffered and can now play
		this.isSpeakerBuffering = false;

		this.onBufferingStateUpdated();
	}

	private onBufferingStateUpdated(): void {
		//sanity checks
		if (this.isSpeakerBuffering && !this.speakerPlayer.isBuffering) {
			this.isSpeakerBuffering = false;
		}

		if (this.isContentBuffering && (!this.activeContentPlayer || !this.activeContentPlayer.isBuffering)) {
			this.isContentBuffering = false;
		}

		//convey the combined buffering state through the player
		const isBuffering: boolean = this.isSpeakerBuffering || this.isContentBuffering;

		this.API.isBufferingOverride = isBuffering;

		console.info(`${isBuffering} = speaker[${this.isSpeakerBuffering}] content[${this.isContentBuffering}]`);

		//inform the players of the dual buffering state
		if (isBuffering) {
			if (this.activeContentPlayer && this.activeContentPlayer.isActive) {
				this.speakerPlayer.dualBufferingStart();
				this.activeContentPlayer.dualBufferingStart();
			}
		} else { //both are good to go, so play
			this.speakerPlayer.dualBufferingEnd();

			if (this.activeContentPlayer) {
				this.activeContentPlayer.dualBufferingEnd();
			}
		}

		//workaround timing of isBuffering change watcher so that analytics behave as expected
		window.setTimeout(() => this.API.isBuffering = isBuffering, 10);
	}

	private onSpeakerPlayerPause(): void {
		if (this.activeContentPlayer && !this.isContentBuffering) {
			this.activeContentPlayer.pause();
		}

		if (this.isLive) {
			if (this.isContentTrackedInMasterPlaylist) {
				this.masterPlaylistLoader.stop();
			}

			this.cleanUpContentPlayers();
		}
	}

	private onSpeakerPlayerPlay(): void {
		if (this.activeContentPlayer) {
			this.activeContentPlayer.play();
		}

		this.masterPlaylistLoader.start();
	}

	private spinUpContentPlayer(cue: ContentCue): DualHlsContentPlayer {
		const { contentPlaylist } = cue;

		console.info('spin up content player for ' + contentPlaylist.start);

		//content playlist should not retry if end tag is received.
		const { config } = this.API || {};
		config.hls = { ...config.hls, disableLiveKeepAlive: true };

		const contentPlayer = new DualHlsContentPlayer(this.API, cue, this.contentPlayerContainer, this.API.activeSource.isLive, this.isMulticast, this.speakerPlayer.pLoader, this.speakerPlayer.fLoader, this.speakerPlayer.nextLoadLevel, this.revConnectInfo);

		this.contentPlayers.set(this.getFormattedSecStr(contentPlaylist.start), contentPlayer);

		return contentPlayer;
	}

	private setActiveContentPlayer(contentPlayer: DualHlsContentPlayer): void {
		this.API.dualPlaybackLayout = this.API.dualPlaybackLayout || DUAL_PLAYBACK_LAYOUT_OPTIONS.find(option => option.isDefault);
		this.API.isDualPlaybackActive = true;

		if (this.activeContentPlayer !== contentPlayer) {
			this.isContentBuffering = false;

			this.activeContentPlayer = contentPlayer;
			this.activeContentPlayer.on(HLS_PLAYER_BUFFERING, () => this.onContentPlayerBuffering());
			this.activeContentPlayer.on(HLS_PLAYER_CAN_PLAY, () => this.onContentPlayerCanPlay());
			this.activeContentPlayer.on(HLS_PLAYER_LEVELS_UPDATED, data => this.onContentPlayerLevelsUpdated(data));
			this.activeContentPlayer.setLevel(this.speakerPlayer.nextLoadLevel);
			this.onBufferingStateUpdated();
		}

		this.onContentPlayerLevelsUpdated(this.activeContentPlayer.getLevelsModel());
	}

	private onContentPlayerBuffering(): void {
		if (this.isContentLoadingAfterEnded()) {
			console.warn('content player got into false buffering');
			this.contentCueTrack.resetActiveCues();
			return;
		}
		console.info('content buffering');
		this.isContentBuffering = true;

		this.onBufferingStateUpdated();
	}

	/**
	* Below workaround needs to be revisited later. I added a method stopActiveStreamLoading in DualHlsContentPlayer
	* which protects content player from getting into false buffering mode when user stops sharing.
	* -------------------------------------------------------------
	* REV-2528 - when user stops sharing, content player gets into buffering mode and keeps showing spinner.
	* work around to check the contentPlaylist end time and if it is there then considered it finished.
	*/
	private isContentLoadingAfterEnded(): boolean {
		// Prevents false buffering when content has ended but player keeps requesting data.
		if (!this.isLive || !this.activeContentPlayer || !this.contentCueTrack) {
			return false;
		}

		const speakerDateTime = this.speakerPlayer.getCurrentProgramDateTime();
		const activePlayCue = this.contentCueTrack.getActivePlayCue(this.activeContentPlayer.cue.endSecs);

		let timeDiff = Number.POSITIVE_INFINITY;
		if (speakerDateTime && activePlayCue?.endDateTime) {
			timeDiff = Math.abs(activePlayCue.endDateTime.getTime() - speakerDateTime.getTime());
		}

		const samePlaylistCount = this.activeContentPlayer.samePlaylistCount || 0;
		const isNoActiveCues = this.contentCueTrack.isNoActiveCues;

		return isNoActiveCues && (
			(samePlaylistCount > 3 && timeDiff < 2000) ||
			samePlaylistCount > 8
		);
	}

	private onContentPlayerCanPlay(): void {
		console.info('content canplay');
		this.isContentBuffering = false;

		this.onBufferingStateUpdated();
	}

	private cleanUpContentPlayer(contentPlayer: DualHlsContentPlayer, transitionTime: string, isDestroy: boolean): void {
		console.info('clean up content player');

		if (isDestroy) {
			contentPlayer.destroy();
			this.contentPlayers.delete(transitionTime);
		}

		if (this.activeContentPlayer === contentPlayer) {
			this.activeContentPlayer.removeAllListeners(HLS_PLAYER_BUFFERING);
			this.activeContentPlayer.removeAllListeners(HLS_PLAYER_CAN_PLAY);
			this.activeContentPlayer.removeAllListeners(HLS_PLAYER_LEVELS_UPDATED);

			this.API.isDualPlaybackActive = false;

			this.activeContentPlayer = null;

			this.isContentBuffering = false;
			this.onBufferingStateUpdated();

			this.onContentPlayerLevelsUpdated(null);
		} else if (!this.activeContentPlayer) {
			this.onBufferingStateUpdated(); // just in case
		}
	}

	private getFormattedSecStr(secsNumber: number): string {
		return secsNumber.toFixed(SECONDS_NUMBER_PRECISION);
	}

	public destroy(): void {
		document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);

		this.masterPlaylistLoader.stop();

		this.contentPlayers.forEach(contentPlayer => contentPlayer.destroy());

		if (this.contentCueTrack) {
			this.contentCueTrack.destroy();
		}

		if (this.speakerPlayer) {
			this.speakerPlayer.removeAllListeners(HLS_PLAYER_BUFFERING);
			this.speakerPlayer.removeAllListeners(HLS_PLAYER_CAN_PLAY);

			this.speakerPlayerEventHandlers.forEach(eventHandler => this.speakerPlayer.videoElement.removeEventListener(eventHandler.event, eventHandler.handler));

			this.speakerPlayer.destroy();
			this.speakerPlayer = null;
		}

		this.API.isBufferingOverride = false;
	}
}
